import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";

/**
 * The loading vocabulary. Every "waiting" state in the app comes from here.
 *
 * There are four of them and they are NOT interchangeable, which is the whole
 * reason this file exists rather than one `<Loading />`:
 *
 *   Spinner        the glyph. Never used directly by a screen.
 *   LoadingInline  a few words inside existing layout — a card, a row.
 *   LoadingRegion  the body of a route while it resolves.
 *   Skeleton       a shape-matched placeholder, when the layout is known.
 *
 * Collapsing these into one component would mean either a spinner where a
 * skeleton belongs — the page visibly jumps when content arrives — or a
 * skeleton where a spinner belongs, which invents a layout that never appears.
 *
 * Three behaviours are centralised here because every hand-rolled
 * `if (isPending) return <p>Loading…</p>` got at least one of them wrong:
 *
 *   1. ANTI-FLASH. An indicator shown for 80ms is worse than none: the user
 *      sees a flicker and reads it as jank. Nothing renders for the first
 *      `delayMs`, so a fast response shows no spinner at all.
 *   2. REDUCED MOTION. `animate-pulse` and a spinning ring are exactly the
 *      kind of looping animation `prefers-reduced-motion` exists to stop.
 *      Every animation here is `motion-safe:` only.
 *   3. SCREEN READERS. Decorative shapes are `aria-hidden`, so a skeleton is
 *      not announced as a wall of empty boxes; the status text is announced
 *      once, politely, instead.
 */

/**
 * flashThresholdMs is how long a response may take before a spinner is worth
 * showing. Under ~200ms a human reads the change as instant and an indicator
 * as a glitch; past it, silence reads as a broken button.
 */
const flashThresholdMs = 200;

/** useDelayed reports whether `delayMs` has elapsed since mount. */
function useDelayed(delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(delayMs === 0);

  useEffect(() => {
    if (delayMs === 0) return;
    setElapsed(false);
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  return elapsed;
}

export interface SpinnerProps {
  /** `sm` sits on a line of text, `md` beside a heading, `lg` alone in a region. */
  size?: "sm" | "md" | "lg";
  className?: string;
}

const spinnerSize: Record<NonNullable<SpinnerProps["size"]>, string> = {
  sm: "size-3.5 border",
  md: "size-5 border-2",
  lg: "size-8 border-2",
};

/**
 * The indicator glyph, and nothing else.
 *
 * `aria-hidden` always: it is decoration. The surrounding component owns the
 * announcement, because a spinner that announces itself either says nothing
 * useful ("image") or repeats on every re-render.
 *
 * A bordered element rather than an SVG — it is three classes, scales with
 * `size-*`, and inherits `currentColor`, so it needs no fill plumbing and adds
 * nothing to the bundle.
 */
export function Spinner({ size = "sm", className }: SpinnerProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block shrink-0 rounded-full border-current border-r-transparent align-[-0.125em]",
        // motion-safe only: a looping rotation is precisely what
        // prefers-reduced-motion asks us not to do. Without the animation the
        // ring is still a visible "something is happening" mark.
        "motion-safe:animate-spin",
        spinnerSize[size],
        className,
      )}
    />
  );
}

export interface LoadingInlineProps {
  /** Announced to assistive tech and shown beside the spinner. */
  label: string;
  /** Hide the text and show only the spinner; the label is still announced. */
  hideLabel?: boolean;
  size?: SpinnerProps["size"];
  delayMs?: number;
  className?: string;
}

/**
 * A waiting state that lives inside existing layout — a card body, a table
 * row, next to a control. It does not reserve space or centre anything.
 */
export function LoadingInline({
  label,
  hideLabel = false,
  size = "sm",
  delayMs = flashThresholdMs,
  className,
}: LoadingInlineProps) {
  const show = useDelayed(delayMs);
  if (!show) return null;

  return (
    <p
      role="status"
      className={cn(
        "inline-flex items-center gap-2 text-[13px] text-fg-muted",
        className,
      )}
    >
      <Spinner size={size} />
      <span className={cn(hideLabel && "sr-only")}>{label}</span>
    </p>
  );
}

export interface LoadingRegionProps {
  label: string;
  delayMs?: number;
  className?: string;
}

/**
 * A waiting state that occupies the body of a route or a large panel.
 *
 * Deliberately has generous vertical padding rather than filling the viewport:
 * the app shell's header stays put, so the page does not appear to reload when
 * content arrives.
 */
export function LoadingRegion({
  label,
  delayMs = flashThresholdMs,
  className,
}: LoadingRegionProps) {
  const show = useDelayed(delayMs);
  if (!show) return null;

  return (
    <section
      role="status"
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-4 py-16 text-fg-muted sm:px-6",
        className,
      )}
    >
      <Spinner size="lg" />
      <p className="text-sm">{label}</p>
    </section>
  );
}

export interface SkeletonProps {
  className?: string;
}

/**
 * One shape-matched placeholder block.
 *
 * `aria-hidden` is not optional. A skeleton is a drawing of content that does
 * not exist yet; announced, it is a run of empty regions that tells a screen
 * reader user nothing. Pair a group of these with `SkeletonStatus` so the wait
 * is announced once, in words.
 *
 * Callers set the dimensions, because the point of a skeleton is that it
 * matches the thing it stands in for. A skeleton with its own idea of the
 * layout produces a visible jump when the real content lands.
 */
export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "rounded-md bg-bg-elevated motion-safe:animate-pulse",
        className,
      )}
    />
  );
}

/**
 * The announcement for a group of Skeletons, which are themselves silent.
 *
 * Rendered visually hidden: the skeleton already communicates "loading" to
 * anyone who can see it, and duplicating that in text would be noise.
 */
export function SkeletonStatus({ label }: { label: string }) {
  return (
    <p role="status" className="sr-only">
      {label}
    </p>
  );
}
